import * as React from "react"
import { useRecoilValue } from "recoil"
import { VideoPlayer } from "./videoPlayer"
import { LocalUserProperties } from "../../types/localUserProps"
import { useLocalUserType } from "../../hooks/useLocalUserType"
import { useWatchParty } from "../../hooks/useWatchParty"
import useLeaveOverlay from "../../hooks/useLeaveOverlay"
import { watchPartyHideControlsSelector } from "../../recoil/atoms/watchPartyScreen"
import { playerPropsSelector } from "../../recoil/atoms/player"
import { productOverlaySelector } from "../../recoil/atoms/product-overlay"
import { usersListSmallWindowSelector } from "../../recoil/atoms/userListSmallWindow"
import { brandingSelector } from "../../recoil/atoms/room"
import useWindowSize from "../../hooks/useWindowSize"
import { isAllowedNow, wasAllowedBefore } from "../../util"
import layoutStates from "../../recoil/atoms/layout"

const UserPermission = window.MeetOnline?.UserPermission

interface MainVideoScreenProps {
  localUserProperties: LocalUserProperties
  isLeaveOverlayEnable: boolean
  isTablet: boolean
  overlayWidth: number
  maxHeightMainScreen: number
  widthMainScreen: string | number
  isWithoutTitle: boolean
  isSmallMainScreen: boolean
  isMediumAuctionBadge: boolean
  maxWidthOverlay: number
}

const _MainVideoScreen: React.FC<MainVideoScreenProps> = ({
  localUserProperties,
  isLeaveOverlayEnable,
  isTablet,
  overlayWidth,
  maxHeightMainScreen,
  widthMainScreen,
  isWithoutTitle,
  isSmallMainScreen,
  isMediumAuctionBadge,
  maxWidthOverlay
}) => {
  const { isModerator } = useLocalUserType()
  const { screenSize } = useWindowSize()
  const { watchPartyHostId, isWatchParty, isAudioWatchParty } = useWatchParty()
  const { showLeaveOverlay, setShowLeaveOverlay } = useLeaveOverlay(true, false) //Need check

  const roomStyles = useRecoilValue(brandingSelector)
  const { isOpen } = useRecoilValue(productOverlaySelector)
  const { isOpenControlDesktop } = useRecoilValue(playerPropsSelector)
  const hideControls = useRecoilValue(watchPartyHideControlsSelector)
  const userIdsSmallWindows = useRecoilValue(usersListSmallWindowSelector)
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)

  const isBorderVideoPlayer = React.useMemo(() => {
    return (
      !isTablet &&
      (watchPartyHostId === "modNotPresentVideo" ||
        (isWatchParty &&
          userIdsSmallWindows.length === 0 &&
          !isAudioWatchParty))
    )
  }, [
    isAudioWatchParty,
    isTablet,
    isWatchParty,
    userIdsSmallWindows.length,
    watchPartyHostId
  ])

  const heightLeaveOverlay = React.useMemo<string>(
    () =>
      !(!isModerator && hideControls) &&
        watchPartyHostId !== "modNotPresentImage" &&
        watchPartyHostId !== "modNotPresentOverlay" &&
        isOpenControlDesktop &&
        !isOpen
        ? "calc(100% - 55px)"
        : "100%",
    [isModerator, hideControls, watchPartyHostId, isOpenControlDesktop, isOpen]
  )

  const borderRadiusTabletScreen = React.useMemo(() => {
    return watchPartyHostId === "modNotPresentVideo" ||
      userIdsSmallWindows.length === 0
      ? "0px"
      : widthMainScreen >= screenSize.width
        ? "0px"
        : "0.375rem"
  }, [
    screenSize.width,
    userIdsSmallWindows.length,
    watchPartyHostId,
    widthMainScreen
  ])

  const isLogo = React.useMemo(() => {
    if (
      !isTablet &&
      roomStyles?.showLogo &&
      roomStyles.logo &&
      !isOpen
    ) {
      return true
    }
    return false
  }, [isTablet, roomStyles?.showLogo, roomStyles.logo, isOpen])

  const tabletStyling: React.CSSProperties = React.useMemo(() => {
    return {
      maxWidth: isFloatingDivActive ? "100%" : widthMainScreen,
      height: "100%",
      zIndex: 14,
      backgroundColor: !isWatchParty
        ? roomStyles?.alertBackgroundColor ?? "black"
        : "transparent",
      width: "100%",
      borderRadius: borderRadiusTabletScreen
    }
  }, [
    borderRadiusTabletScreen,
    isWatchParty,
    maxHeightMainScreen,
    roomStyles?.alertBackgroundColor,
    widthMainScreen,
    isFloatingDivActive
  ])

  const desktopStyling: React.CSSProperties = React.useMemo(() => {
    if (isWatchParty) {
      return {
        width: "100%",
        height: "100%",
        borderRadius: "0.375rem"
      }
    } else {
      return {
        width: "100%",
        height: "100%",
        display: "none",
        borderRadius: "0.375rem"
      }
    }
  }, [isWatchParty])

  React.useEffect(() => {
    if (
      isTablet &&
      !isModerator &&
      ((isAllowedNow(
        localUserProperties.permissions,
        UserPermission.canUseMic
      ) &&
        !wasAllowedBefore(
          localUserProperties.oldPermissions,
          UserPermission.canUseMic
        )) ||
        (isAllowedNow(
          localUserProperties.permissions,
          UserPermission.canUseCam
        ) &&
          !wasAllowedBefore(
            localUserProperties.oldPermissions,
            UserPermission.canUseCam
          )))
    ) {
      setShowLeaveOverlay(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localUserProperties.permissions])

  return (
    <>
      <VideoPlayer
        localUserProperties={localUserProperties}
        showLeave={isLeaveOverlayEnable}
        isTablet={isTablet}
        isDesktop={true}
        isMainMobileScreen={isTablet}
        isTouchDelay={true}
        isMobile={isTablet ? true : undefined}
        maxHeightMainScreen={maxHeightMainScreen}
        setShowLeaveOverlay={setShowLeaveOverlay}
        showLeaveOverlay={isLeaveOverlayEnable && showLeaveOverlay}
        isWithoutTitle={isWithoutTitle}
        widthMainScreen={widthMainScreen}
        overlayWidth={overlayWidth}
        isSmallMainScreen={isSmallMainScreen}
        maxWidthOverlay={maxWidthOverlay}
        heightLeaveOverlay={heightLeaveOverlay}
        styling={isTablet ? tabletStyling : desktopStyling}
        isMediumAuctionBadge={isMediumAuctionBadge}
        isBorderVideoPlayer={isBorderVideoPlayer}
        isLogo={isLogo}
        showFullscreenPlayerButtons={screenSize.width > screenSize.height}
      />
    </>
  )
}

export const MainVideoScreen = React.memo(_MainVideoScreen)
